import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, ExternalLink, Trophy, Users, CheckCircle,
  TrendingUp, Link2, RefreshCw, Copy, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEP_NAMES = {
  1: 'Poop Frequency', 2: 'Symptoms', 3: 'Meet Hashi-aid', 4: 'Support Areas',
  5: 'Whole Body Health', 6: 'Current Supplements', 7: 'Absorption Info',
  8: 'Comparison', 9: 'Clinical Research', 10: 'Timeline', 11: 'Email Capture'
};

const SplitTestResults = () => {
  const { token } = useAuth();
  const { id } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const quizBaseUrl = window.location.origin;

  const fetchResults = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/split-tests/${id}/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setResults(data);
    } catch (err) {
      console.error('Failed to fetch results:', err);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'linear-gradient(135deg, #7874F8, #09C9E9)' }} />
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <p className="text-gray-500">Test not found</p>
      </div>
    );
  }

  const { test, control, variant, winner, sample_size, confidence } = results;
  const quizUrl = test.slug ? `${quizBaseUrl}/${test.slug}` : `${quizBaseUrl}/`;

  const confidenceColors = {
    low: { bg: 'rgba(245, 158, 11, 0.15)', text: '#fbbf24' },
    medium: { bg: 'rgba(59, 130, 246, 0.15)', text: '#60a5fa' },
    high: { bg: 'rgba(16, 185, 129, 0.15)', text: '#34d399' }
  };
  const confStyle = confidenceColors[confidence] || confidenceColors.low;

  const winnerColors = {
    control: { bg: 'rgba(120, 116, 248, 0.15)', text: '#a5a2ff', label: 'Control Winning' },
    variant: { bg: 'rgba(9, 201, 233, 0.15)', text: '#22d3ee', label: 'Variant Winning' },
    tie: { bg: 'rgba(107, 114, 128, 0.15)', text: '#9ca3af', label: 'Too Close to Call' }
  };
  const winStyle = winnerColors[winner] || winnerColors.tie;

  const controlMax = Math.max(...control.funnel.map(f => f.views), 1);
  const variantMax = Math.max(...variant.funnel.map(f => f.views), 1);

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }}>
      <header className="sticky top-0 z-50 border-b" style={{ background: '#0f0f17', borderColor: '#1e1e2e' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/split-tests" className="text-gray-500 hover:text-gray-300">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-gray-100" data-testid="test-results-title">{test.name}</h1>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: winStyle.bg, color: winStyle.text }}>{winStyle.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: confStyle.bg, color: confStyle.text }}>
                  {confidence} confidence ({sample_size} sessions)
                </span>
              </div>
            </div>
          </div>
          <button onClick={fetchResults} className="p-2 rounded-lg hover:bg-white/5" style={{ border: '1px solid #2a2a3a' }}>
            <RefreshCw className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Test Info Bar */}
        <div className="rounded-xl p-5 mb-6 flex flex-wrap items-center gap-6" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-gray-400">Quiz URL:</span>
            <code className="text-sm text-cyan-400">{quizUrl}</code>
            <button onClick={() => copyUrl(quizUrl)} className="text-gray-600 hover:text-gray-400">
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-gray-400">LP Link:</span>
            {test.lp_link ? (
              <a href={test.lp_link} target="_blank" rel="noreferrer" className="text-sm text-cyan-400 hover:underline">{test.lp_link}</a>
            ) : (
              <span className="text-sm text-gray-600">Using default</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">Ratio:</span>
            <span className="text-sm text-gray-300">{test.ratio?.[0]}% / {test.ratio?.[1]}%</span>
          </div>
        </div>

        {/* Side by Side Metrics */}
        <div className="grid grid-cols-2 gap-5 mb-8">
          {/* Control */}
          <div className="rounded-xl p-6" style={{ background: '#13131a', border: winner === 'control' ? '2px solid #7874F8' : '1px solid #1e1e2e' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-200">Control (Original)</h3>
              {winner === 'control' && <Trophy className="w-5 h-5 text-purple-400" />}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Sessions</p>
                <p className="text-2xl font-bold" style={{ color: '#7874F8' }}>{control.sessions}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Completions</p>
                <p className="text-2xl font-bold text-gray-200">{control.completions}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Conversion</p>
                <p className="text-2xl font-bold" style={{ color: '#7874F8' }}>{control.conversion_rate}%</p>
              </div>
            </div>
          </div>

          {/* Variant */}
          <div className="rounded-xl p-6" style={{ background: '#13131a', border: winner === 'variant' ? '2px solid #09C9E9' : '1px solid #1e1e2e' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-200">Variant (Modified)</h3>
              {winner === 'variant' && <Trophy className="w-5 h-5 text-cyan-400" />}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Sessions</p>
                <p className="text-2xl font-bold" style={{ color: '#09C9E9' }}>{variant.sessions}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Completions</p>
                <p className="text-2xl font-bold text-gray-200">{variant.completions}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Conversion</p>
                <p className="text-2xl font-bold" style={{ color: '#09C9E9' }}>{variant.conversion_rate}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Funnel Comparison */}
        <div className="grid grid-cols-2 gap-5">
          <div className="rounded-xl p-6" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Control Funnel</h3>
            <div className="space-y-2">
              {control.funnel.map((step, i) => (
                <div key={step.step} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-5">{step.step}</span>
                  <div className="flex-1">
                    <div className="h-5 rounded overflow-hidden" style={{ background: '#1a1a26' }}>
                      <div className="h-full rounded" style={{ width: `${(step.views / controlMax) * 100}%`, background: 'linear-gradient(90deg, #7874F8, #a5a2ff)' }} />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{step.views}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl p-6" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Variant Funnel</h3>
            <div className="space-y-2">
              {variant.funnel.map((step, i) => (
                <div key={step.step} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-5">{step.step}</span>
                  <div className="flex-1">
                    <div className="h-5 rounded overflow-hidden" style={{ background: '#1a1a26' }}>
                      <div className="h-full rounded" style={{ width: `${(step.views / variantMax) * 100}%`, background: 'linear-gradient(90deg, #09C9E9, #67e8f9)' }} />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{step.views}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modified Steps Detail */}
        {test.step_overrides && Object.keys(test.step_overrides).length > 0 && (
          <div className="rounded-xl p-6 mt-6" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Modified Steps in Variant</h3>
            <div className="space-y-3">
              {Object.entries(test.step_overrides).map(([stepNum, data]) => (
                <div key={stepNum} className="p-3 rounded-lg" style={{ background: '#1a1a26' }}>
                  <p className="text-sm font-medium text-gray-300 mb-1">Step {stepNum}: {STEP_NAMES[stepNum]}</p>
                  {data.question && <p className="text-xs text-gray-500">Question: <span className="text-cyan-400">{data.question}</span></p>}
                  {data.subtitle && <p className="text-xs text-gray-500">Subtitle: <span className="text-cyan-400">{data.subtitle}</span></p>}
                  {data.options?.length > 0 && (
                    <p className="text-xs text-gray-500">Options: <span className="text-cyan-400">{data.options.filter(o => o).join(', ')}</span></p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SplitTestResults;
